if (!customElements.get('dd-feature-media-video')) {
  customElements.define('dd-feature-media-video', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.video = this.querySelector('[data-dd-feature-video]');
      if (!this.video) return;
      this.poster = this.querySelector('[data-dd-feature-poster]');
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      const sync = () => this.sync();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', sync, options);
      document.addEventListener('visibilitychange', sync, options);
      window.addEventListener('resize', sync, options);
      window.addEventListener('orientationchange', sync, options);
      this.poster?.addEventListener('load', sync, options);
      this.poster?.addEventListener('error', sync, options);
      this.video.addEventListener('pointerdown', () => this.prepare(true), options);
      this.video.addEventListener('focus', () => this.prepare(true), options);
      this.video.addEventListener('play', () => {
        this.userPlaying = !this.automaticPlay;
        this.automaticPlay = false;
        this.userPaused = false;
        if (!this.eligible()) this.stop();
      }, options);
      this.video.addEventListener('playing', () => {
        if (!this.eligible()) return this.stop();
        if (this.poster) this.poster.hidden = true;
      }, options);
      this.video.addEventListener('pause', () => {
        if (this.policyPause) this.policyPause = false;
        else this.userPaused = true;
        this.userPlaying = false;
      }, options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        if (this.poster && !this.video.poster) this.poster.hidden = false;
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(sync, { threshold: [0, 0.1] });
        this.observer.observe(this);
      } else {
        window.addEventListener('scroll', sync, { ...options, passive: true });
      }
      this.sync();
    }

    disconnectedCallback() {
      this.stop();
      this.observer?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
      this.policyPause = false;
      this.automaticPlay = false;
    }

    eligible() {
      if (!this.isConnected || document.hidden) return false;
      const box = this.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0 || getComputedStyle(this).visibility === 'hidden') return false;
      const width = Math.max(0, Math.min(box.right, window.innerWidth) - Math.max(box.left, 0));
      const height = Math.max(0, Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0));
      return width * height >= box.width * box.height * 0.1;
    }

    prepare(deliberate = false) {
      if (!this.eligible() || (!deliberate && this.poster && !this.poster.complete)) return false;
      if (this.poster?.complete && this.poster.naturalWidth > 0) {
        this.video.poster = this.poster.currentSrc || this.poster.src;
      }
      if (!this.video.hasAttribute('data-dd-hydrated')) {
        this.video.querySelectorAll('source[data-dd-src]').forEach((source) => {
          source.src = source.dataset.ddSrc;
        });
        this.video.setAttribute('data-dd-hydrated', '');
        this.video.load();
      }
      if (this.video.controls && this.poster && (this.video.poster || this.poster.complete)) this.poster.hidden = true;
      return true;
    }

    stop() {
      this.playToken = (this.playToken || 0) + 1;
      this.playPending = false;
      this.userPlaying = false;
      if (this.video && !this.video.paused) {
        this.policyPause = true;
        this.video.pause();
      }
    }

    sync() {
      if (!this.eligible()) return this.stop();
      if (this.video.controls) this.prepare();
      if (this.dataset.playback !== 'autoplay') return;
      if (this.motion.matches) {
        if (!this.userPlaying) this.stop();
        return;
      }
      if (this.userPaused || this.failed || this.playPending || !this.video.paused || !this.prepare()) return;
      const token = this.playToken = (this.playToken || 0) + 1;
      this.playPending = true;
      this.video.muted = true;
      this.automaticPlay = true;
      Promise.resolve(this.video.play()).catch(() => {
        if (this.playToken === token) this.automaticPlay = false;
      }).finally(() => {
        if (this.playToken !== token) return;
        this.playPending = false;
        if (!this.eligible() || (this.motion.matches && !this.userPlaying)) this.stop();
      });
    }
  });
}
